import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@benchlabs/react-design-system';
import { FC, ChangeEvent, FormEvent, useState } from 'react';

interface LedgerSelectSurveyProps {
  onSurveySubmit: (
    isSurveySubmitted: boolean,
    selectedSurveyOption: string,
    otherReason?: string,
  ) => void;
}

export const LedgerSelectSurvey: FC<LedgerSelectSurveyProps> = ({ onSurveySubmit }) => {
  const [selectedSurveyOption, setSelectedSurveyOption] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [helperText, setHelperText] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const surveyOption = event.target.value;
    setSelectedSurveyOption(surveyOption);
    setHelperText('');
    setError(false);
    if (surveyOption !== 'other') setOtherReason('');
  };

  const handleSurveySubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedSurveyOption) {
      setHelperText('Please select an option.');
      setError(true);
      return;
    }
    if (selectedSurveyOption === 'other' && !otherReason.trim()) {
      setHelperText('Please specify the reason when selecting "Other".');
      setError(true);
      return;
    }
    setError(false);
    setHelperText('Thank you. Your response has been recorded.');
    onSurveySubmit(
      true,
      selectedSurveyOption,
      selectedSurveyOption === 'other' ? otherReason : undefined,
    );
    setSelectedSurveyOption('');
    setOtherReason('');
  };

  return (
    <form onSubmit={handleSurveySubmit} aria-label="Ledger Selection Survey">
      <FormControl error={error}>
        <FormLabel id="select-ledger-account-selection-reason">
          Please specify the reason for enabling ledger account selection:
        </FormLabel>
        <RadioGroup
          aria-label="options"
          name="options"
          value={selectedSurveyOption}
          onChange={handleOptionChange}
        >
          <FormControlLabel
            value="Institution has no account numbers"
            control={<Radio />}
            label="Institution does not have account numbers (Ex: Brex)"
          />
          <FormControlLabel
            value="Statement from old account number"
            control={<Radio />}
            label="Processing statement from old account number"
          />
          <FormControlLabel
            value="Duplicate ledgers found"
            control={<Radio />}
            label="Duplicate ledgers found"
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label={
              selectedSurveyOption === 'other' ? (
                <TextField
                  value={otherReason}
                  label="Please Specify"
                  onChange={event => setOtherReason(event.target.value)}
                  required
                  inputProps={{ 'aria-label': 'Other reason' }}
                  autoFocus
                />
              ) : (
                'Other'
              )
            }
          />
        </RadioGroup>
        <FormHelperText>{helperText}</FormHelperText>
        <Box mt={1}>
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </Box>
      </FormControl>
    </form>
  );
};
